// pages/secure/ExaminerAccessPage.js
// Managing-broker screen for issuing read-only Division inspection credentials
// (68 IAC 1450.610(c)(3)(D)) and reviewing what those accounts have viewed.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import ExaminerScopeModal from '../../components/secure/ExaminerScopeModal';
import DataList from '../../components/shared/DataList';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtWhen = (d) => (d ? new Date(d).toLocaleString() : '—');

const StatusPill = ({ status }) => {
    const map = {
        active: 'bg-green-100 text-green-800',
        expired: 'bg-slate-200 text-slate-600',
        revoked: 'bg-red-100 text-red-700',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || map.expired}`}>{status}</span>;
};

const ExaminerAccessPage = () => {
    const api = useApiClient();
    const [accounts, setAccounts] = useState([]);
    const [log, setLog] = useState([]);
    const [showLog, setShowLog] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState(null);
    const [issued, setIssued] = useState(null);   // one-time password display
    const [saving, setSaving] = useState(false);
    // The scope board: which areas each account has been granted. Kept beside the
    // account list rather than merged into it, because one is who may log in and
    // the other is what they may then read — two decisions, both the broker's.
    const [board, setBoard] = useState({ areas: [], examiners: [] });
    const [scopeFor, setScopeFor] = useState(null);

    const load = useCallback(() => {
        api.get('/api/examiners').then(setAccounts).catch(e => setError(e.message));
        api.get('/api/examiner-access/grants').then(setBoard).catch(e => setError(e.message));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const scopeOf = (acct) => board.examiners.find(e => e.user_id === acct.id) || null;
    const grantedCount = (acct) => (scopeOf(acct)?.areas || []).filter(a => a.status === 'granted').length;
    const pendingCount = (acct) => (scopeOf(acct)?.areas || []).filter(a => a.status === 'requested').length;
    const totalPending = board.examiners.reduce((n, e) => n + (e.pending || 0), 0);

    const openLog = async (userId) => {
        try {
            setLog(await api.get(`/api/examiners/log${userId ? `?user_id=${userId}` : ''}`));
            setShowLog(true);
        } catch (e) { setError(e.message); }
    };

    const issue = async () => {
        if (!form.username) { setError('Username is required.'); return; }
        setSaving(true); setError('');
        try {
            const res = await api.post('/api/examiners', form);
            setForm(null);
            setIssued(res);
            load();
        } catch (e) { setError(e.message); } finally { setSaving(false); }
    };

    const act = async (acct, action) => {
        const label = action === 'revoke' ? `Revoke access for "${acct.username}" immediately?` : null;
        if (label && !window.confirm(label)) return;
        try {
            if (action === 'extend') {
                const days = window.prompt('Extend access for how many days?', '30');
                if (!days) return;
                await api.put(`/api/examiners/${acct.id}`, { action: 'extend', days });
            } else {
                await api.put(`/api/examiners/${acct.id}`, { action });
            }
            load();
        } catch (e) { setError(e.message); }
    };

    const resetPw = async (acct) => {
        if (!window.confirm(`Issue a new temporary password for "${acct.username}"?`)) return;
        try {
            const res = await api.post(`/api/examiners/${acct.id}/reset-password`, {});
            setIssued({ ...res, username: acct.username });
        } catch (e) { setError(e.message); }
    };

    const remove = async (acct) => {
        if (!window.confirm(`Delete the account "${acct.username}"? The access log is kept.`)) return;
        try { await api.delete(`/api/examiners/${acct.id}`); load(); } catch (e) { setError(e.message); }
    };

    const input = 'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500';
    const label = 'block text-sm font-medium text-gray-700 mb-1';

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-start justify-between mb-2">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Division Inspection Access</h1>
                    <p className="text-sm text-gray-500">
                        Read-only credentials for IDFPR / Division of Real Estate staff — 68 Ill. Adm. Code 1450.610(c)(3)(D).
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => openLog(null)} className="text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50">Access log</button>
                    <button onClick={() => setForm({ username: '', email: '', examiner_org: 'IDFPR Division of Real Estate', days: 30 })}
                        className="text-sm px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-900">+ Issue access</button>
                </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 mb-4">
                These accounts can <strong>view</strong> records but cannot create, change, or delete anything —
                every write is refused at the server. A new account reads <strong>nothing</strong> until you
                grant it an area under <em>Scope</em>; the examiner may also request areas, stating why.
                Payroll, operating accounting, commissions, expenses, the Tax Center, the login roster and the
                corporate records book can never be opened to an inspection account, whatever is ticked.
                Access expires automatically on the date shown.
            </div>

            {totalPending > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-sm text-indigo-900 mb-4">
                    {totalPending === 1
                        ? 'One area has been requested and is awaiting your decision.'
                        : `${totalPending} areas have been requested and are awaiting your decision.`}
                </div>
            )}

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                <div className="p-3"><DataList rows={accounts} rowKey="id" csvName="examiner-accounts" defaultSort={{ key: 'last_access', dir: 'desc' }}
                    empty="No inspection accounts. Issue one when the Division requests access."
                    filters={[{ key: 'status', label: 'Status' }, { key: 'examiner_org', label: 'Organization' }]}
                    columns={[
                        { key: 'username', label: 'Username', render: (a) => <span className="font-medium">{a.username}</span> },
                        { key: 'examiner_org', label: 'Organization', render: (a) => a.examiner_org || '—' },
                        { key: 'status', label: 'Status', render: (a) => <StatusPill status={a.status} /> },
                        { key: 'scope', label: 'Scope', type: 'number', value: (a) => grantedCount(a), render: (a) => (
                            <span className="whitespace-nowrap">
                                <button onClick={() => setScopeFor(a)} className="text-indigo-600 hover:underline">
                                    {grantedCount(a) === 0 ? 'No areas' : `${grantedCount(a)} area${grantedCount(a) === 1 ? '' : 's'}`}
                                </button>
                                {pendingCount(a) > 0 && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">{pendingCount(a)} asked</span>}
                            </span>) },
                        { key: 'access_expires_at', label: 'Expires', type: 'date', render: (a) => fmtDate(a.access_expires_at) },
                        { key: 'views', label: 'Views', type: 'number', render: (a) => <button onClick={() => openLog(a.id)} className="text-indigo-600 hover:underline">{a.views}</button> },
                        { key: 'last_access', label: 'Last access', type: 'date', render: (a) => fmtWhen(a.last_access) },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (a) => (
                            <span className="whitespace-nowrap">
                                <button onClick={() => setScopeFor(a)} className="text-slate-600 hover:underline mr-2">Scope</button>
                                {a.status !== 'revoked' && <button onClick={() => act(a, 'revoke')} className="text-red-600 hover:underline mr-2">Revoke</button>}
                                <button onClick={() => act(a, 'extend')} className="text-slate-600 hover:underline mr-2">Extend</button>
                                <button onClick={() => resetPw(a)} className="text-slate-600 hover:underline mr-2">New password</button>
                                <button onClick={() => remove(a)} className="text-red-500 hover:underline">Delete</button>
                            </span>) },
                    ]} /></div>
            </div>

            {scopeFor && (
                <ExaminerScopeModal
                    examiner={scopeOf(scopeFor) || { user_id: scopeFor.id, username: scopeFor.username, areas: [] }}
                    areas={board.areas}
                    onClose={() => setScopeFor(null)}
                    onSaved={() => { setScopeFor(null); load(); }}
                />
            )}

            {/* Issue form */}
            {form && (
                <Modal open title="Issue read-only inspection access" onClose={() => setForm(null)} size="md"
                    footer={<><button onClick={() => setForm(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                        <button onClick={issue} disabled={saving} className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-400">{saving ? 'Issuing…' : 'Issue access'}</button></>}>
                    <div className="space-y-3">
                        <div><label className={label}>Username</label>
                            <input className={input} value={form.username} placeholder="e.g. idfpr.examiner"
                                onChange={e => setForm({ ...form, username: e.target.value })} /></div>
                        <div><label className={label}>Organization</label>
                            <input className={input} value={form.examiner_org} onChange={e => setForm({ ...form, examiner_org: e.target.value })} /></div>
                        <div><label className={label}>Email (optional)</label>
                            <input className={input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                        <div><label className={label}>Access valid for (days)</label>
                            <input type="number" min="1" max="365" className={input} value={form.days}
                                onChange={e => setForm({ ...form, days: e.target.value })} />
                            <p className="text-xs text-slate-500 mt-1">Access stops working automatically after this many days.</p></div>
                    </div>
                </Modal>
            )}

            {/* One-time password */}
            {issued && (
                <Modal open title="Temporary password — shown once" onClose={() => setIssued(null)} size="md"
                    footer={<button onClick={() => setIssued(null)} className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900">Done</button>}>
                    <p className="text-sm text-gray-700 mb-3">
                        Give this to <strong>{issued.username}</strong> directly (phone or in person). It cannot be
                        displayed again, and they will be required to set their own password at first sign-in.
                    </p>
                    <div className="font-mono text-lg bg-slate-100 border border-slate-300 rounded p-3 text-center tracking-wider select-all">
                        {issued.temporary_password}
                    </div>
                    {issued.access_expires_at && (
                        <p className="text-xs text-slate-500 mt-3">Access expires {fmtDate(issued.access_expires_at)}. Sign-in page: businessrealestatecorp.com/portal</p>
                    )}
                </Modal>
            )}

            {/* Access log */}
            {showLog && (
                <Modal open title="Examiner access log" onClose={() => setShowLog(false)} size="lg"
                    footer={<button onClick={() => setShowLog(false)} className="px-4 py-2 text-sm text-gray-600">Close</button>}>
                    <p className="text-sm text-slate-500 mb-3">
                        The brokerage's own record of what was produced during an inspection. Most recent 500 entries.
                    </p>
                    <div className="max-h-[32rem] overflow-y-auto">
                        <DataList rows={log} rowKey="log_id" csvName="examiner-access-log" defaultSort={{ key: 'accessed_at', dir: 'desc' }} empty="No access recorded yet."
                            filters={[{ key: 'username', label: 'User' }, { key: 'method', label: 'Method' }]}
                            rowClass={(l) => (l.blocked ? 'bg-red-50' : '')}
                            columns={[
                                { key: 'accessed_at', label: 'When', type: 'date', render: (l) => <span className="whitespace-nowrap">{fmtWhen(l.accessed_at)}</span> },
                                { key: 'username', label: 'User' },
                                { key: 'method', label: 'Method', render: (l) => `${l.method}${l.blocked ? ' ⛔' : ''}` },
                                { key: 'blocked', label: 'Blocked', value: (l) => (l.blocked ? 'yes' : 'no') },
                                { key: 'path', label: 'Record viewed', render: (l) => <span className="font-mono text-xs">{l.path}</span> },
                                { key: 'ip_address', label: 'IP', render: (l) => l.ip_address || '—' },
                            ]} />
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ExaminerAccessPage;
